import {Component, Input, signal, Signal} from '@angular/core';
import {Observable} from "rxjs";
import {AvatarModule} from "primeng/avatar";
import {DatePipe, NgClass} from "@angular/common";
import {ChatMessage} from "../../../../../shared/interfaces/chat";
import {MarkdownComponent} from "ngx-markdown";
import {SkeletonModule} from "primeng/skeleton";
import {TableModule} from "primeng/table";
import {KPIChartData, KPIChatResponseConsulta} from "../../../../../shared/interfaces/database/chat-ia";
import {ChartContainerComponent} from "./chart-container/chart-container.component";


@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [
    AvatarModule,
    MarkdownComponent,
    SkeletonModule,
    TableModule,
    DatePipe,
    NgClass,
    ChartContainerComponent,
  ],
  template: `
    <div class="col-12 flex flex-column chat-container ">
      <div class="chat-message-prompt">
        <p class="text-white font-13 mb-1">{{ props.prompt }}</p>
        <small class="text-gray-700">{{ props.dateTime | date : 'shortTime' }}</small>
      </div>
      @if (props.response) {
        <div class="dash-card w-full">
          @if (props.response.header) {
            <div class="dash-header">
              <div class="dash-title">{{ props.response.header }}</div>
            </div>
          }
          <div class="dash-body grid w-full">
            @if (!displayChart) {
              <div class="chat-message-chart scrollbar-thin-gray"
                   [ngClass]="[chartsConfigs.length > 0 ? 'col-12' : 'w-0']">
                @for (chart of chartsConfigs; track $index) {
                  @defer (on viewport) {
                    <app-chart-container [value]="chart" [source]="getChartSource(chart)"/>
                  } @loading {
                    <p-skeleton width="70%" height="4rem"
                                styleClass="mb-2 bg-gray-800" borderRadius="9px"/>
                  } @placeholder (minimum 500ms) {
                    <p-skeleton width="70%" height="4rem"
                                styleClass="mb-2 bg-gray-800" borderRadius="9px"/>
                  } @error {
                    <p class="text-white text-lg py-1 ">No se pudo cargar la grafica</p>
                  }
                }
              </div>
            }
            <div class="markdown-container col-12">
              @if (tableColumns.length > 0) {
                <p-table [value]="props.response.data!" [columns]="tableColumns"
                         [paginator]="true" [rows]="5" styleClass="p-datatable-sm">
                  <ng-template pTemplate="header" let-columns>
                    <tr>
                      @for (col of columns; track col) {
                        <th [ngClass]="{'text-right': isNumericColumn(col)}">{{ col }}</th>
                      }
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-rowData let-columns="columns">
                    <tr>
                      @for (col of columns; track col) {
                        <td [ngClass]="{'text-right': isNumericColumn(col)}">{{ rowData[col] }}</td>
                      }
                    </tr>
                  </ng-template>
                </p-table>
              }
              <markdown class="markdown-table" [data]="props.response.tableMarkdown"/>
              <markdown class="markdown-insight" [data]="props.response.bodyMarkdown"/>
            </div>
          </div>
        </div>
      } @else {
        <p-skeleton width="12rem" height="1.2rem"
                    styleClass="mb-2 bg-gray-800" borderRadius="16px"/>
      }
    </div>
  `,
  styleUrl: './chat-message.component.scss'
})
export class ChatMessageComponent {

  @Input('props')
  props!: ChatMessage;

  @Input('chartSource')
  chartSource?: (chart: KPIChatResponseConsulta) => Observable<KPIChartData>;

  title: Signal<string> = signal('');

  get chartsConfigs(): KPIChatResponseConsulta[] {
    return this.props.response?.consultas ?? [];
  }

  get chartIsAvailable(): boolean {
    return this.chartsConfigs.length > 0;
  }

  get hasContent(): boolean {
    const response = this.props.response;
    return !!(response?.header || response?.data?.length || response?.tableMarkdown || response?.bodyMarkdown);
  }

  get displayChart(): boolean {
    return this.hasContent && !this.chartIsAvailable;
  }

  get tableColumns(): string[] {
    const data = this.props.response?.data;
    return data?.length ? Object.keys(data[0]) : [];
  }

  isNumericColumn(col: string): boolean {
    const value = this.props.response?.data?.[0]?.[col];
    if (typeof value === 'number') return true;
    if (typeof value === 'string') return /^-?\d+([.,]\d+)?%?$/.test(value.trim());
    return false;
  }

  getChartSource(chart: KPIChatResponseConsulta): Observable<KPIChartData> | undefined {
    if (!this.chartSource) {
      console.warn('ChatMessageComponent: falta [chartSource], no se puede cargar el gráfico.');
      return undefined;
    }
    return this.chartSource(chart);
  }
}
